/* eslint-disable react/forbid-prop-types,import/no-unresolved */
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useMutation, useQueryClient } from 'react-query';
import { SelectCountryDropdown } from 'assets/components';
import ModalBodyLayout from '../../../shared/ModalBodyLayout';
import { useSetting } from '../../../../utilities/sharedHooks/settings';
import { useUserPersonalInfo } from '../../../../utilities/sharedHooks/user';
import { BANK_WITHDRAW_REQUIRED_FIELDS, WalletScreens } from '../../constants';
import { BANK_WITHDRAW } from '../../mutations';
import { showAlert } from '../../../../utilities/notifications';
import { WALLETS_ALL } from '../../queries';
import { useLedgerFilteredCurrencies } from '../../../../utilities/sharedHooks/ledgerWallets';

import { SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY } from '../../../../utilities/sharedQueries';
import { formatNumber, formatNumberWithComma } from '../../../../utilities/formatHelpers';
import { exchange } from '../../../../utilities/constants';

const FiatWalletWithdrawalDetails = ({ options, onCloseModal, openScreen }) => {
  const { wallet, fiatAmount, fiatAmountWithFees } = options;
  const { t: translate } = useTranslation();
  const [form, setForm] = useState({
    amount: fiatAmount,
    recipientIban: '',
    recipientBic: '',
    recipientBank: '',
    recipientAccountNumber: '',
    recipientSortCode: '',
    recipientBankCountry: '',
    walletAddress: wallet.address,
    otpauth: '',
    coin: wallet.coin,
  });
  const { firstName, lastName } = useUserPersonalInfo();
  const [totpFeatureEnabled] = useSetting('app_features', 'feature_allow_totp');
  const { totpEnabled, totpActive, fa2Activated } = useUserPersonalInfo();
  const queryClient = useQueryClient();

  const tfaEnabled = (totpFeatureEnabled && totpActive && totpEnabled) || fa2Activated;

  const { data: supportedCurrencies } = useLedgerFilteredCurrencies([
    SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  ]);

  const allowBankWithdrawal = useMemo(() => {
    if (supportedCurrencies && supportedCurrencies[wallet?.coin]) {
      return !!supportedCurrencies[wallet?.coin]?.bank_transfer_fee;
    }
    return false;
  }, [supportedCurrencies, wallet]);

  const selectDropDownFields = [
    {
      type: 'img',
      data: 'iso3166alpha2',
      class: 'col-1 text-left position-relative',
      img: {
        sourcePath: './images/flags/1x1/',
        ext: '.svg',
        class: 'rounded-circle position-absolute top-50 start-0 translate-middle-y',
      },
    },
    {
      type: 'text',
      data: 'displayName',
      class: 'col-11 ps-2 text-left',
    },
  ];

  const { mutate: requestBankWithdrawal, isLoading } = useMutation(BANK_WITHDRAW, {
    onSettled: (_, error) => {
      if (error) {
        showAlert({ message: error.message, type: 'danger' });
      } else {
        onCloseModal();
        showAlert({
          message: `${translate(
            'fiat_withdrawal_success_label'
          )} ${fiatAmount} ${wallet.coin.toUpperCase()}`,
          type: 'success',
        });
        queryClient.resetQueries(WALLETS_ALL);
      }
    },
  });

  const requiredFields = BANK_WITHDRAW_REQUIRED_FIELDS[wallet.coin];

  const handleSubmitForm = (e) => {
    e.preventDefault();
    if (tfaEnabled) {
      openScreen(WalletScreens.VERIFY_TRANSACTION, { ...options, ...form });
    } else {
      requestBankWithdrawal({ ...options, ...form, otpauth: null });
    }
    return true;
  };

  const clearError = (e) => {
    if (!e.target) {
      e.className = e.target.className.replace('has-error', '');
    } else {
      e.target.className = e.target.className.replace('has-error', '');
    }
  };

  const handleBlur = (e) => {
    clearError(e);
  };

  const handleChange = (e, fieldName) => {
    if (!e) return;
    if (!e.target) {
      setForm({ ...form, [fieldName]: e });
    } else {
      const { name, value } = e.target;
      setForm({ ...form, [name]: value });
      handleBlur(e);
    }
  };

  const renderFooter = () => (
    <div className="d-flex flex-column gap-1">
      <div className="alert alert-warning" role="alert">
        <i className="icon icon-info-icon" />
        <span className="content">{translate('fiat_withdrawal_note')}</span>
      </div>
      <button
        loading={isLoading ? 'true' : 'false'}
        className="btn btn-primary w-100"
        type="submit"
        form="fiat-wallet-withdrawal">
        {translate('confirm_button_label')}
      </button>
    </div>
  );

  return (
    <ModalBodyLayout
      withBack
      onPrevClick={() =>
        allowBankWithdrawal
          ? openScreen(WalletScreens.FIAT_WITHDRAWAL_REVIEW, { ...options })
          : openScreen(WalletScreens.FIAT_WITHDRAWAL, { ...options })
      }
      title={translate('fiat_withdrawal_details_title')}
      renderFooter={renderFooter}
      isScrollable={false}>
      <div>
        <p className="text-primary h1 upper-case mt-4 mb-5">
          <b>
            {`${formatNumberWithComma(
              formatNumber(
                allowBankWithdrawal ? fiatAmountWithFees : fiatAmount,
                exchange.FIAT_VALUE
              )
            )} ${wallet.coin}`}
          </b>
        </p>
        <p>{translate('fiat_withdrawal_details_form_title')}</p>
        <form onSubmit={handleSubmitForm} id="fiat-wallet-withdrawal">
          <div className="form-group mt-4 mb-0">
            <input
              className="form-control-input bg-transparent"
              placeholder={translate('fiat_withdrawal_account_holder_input_label')}
              defaultValue={`${firstName} ${lastName}`}
              required
              readOnly
            />
            <label className="floating-label">
              {translate('fiat_withdrawal_account_holder_input_label')}
            </label>
          </div>
          {requiredFields.includes('recipientIban') && (
            <div className="form-group mt-5 mb-0">
              <input
                className="form-control-input"
                placeholder={translate('fiat_withdrawal_recipient_iban_input_label')}
                value={form.recipientIban}
                onChange={handleChange}
                name="recipientIban"
                onBlur={handleBlur}
                required
              />
              <label className="floating-label">
                {translate('fiat_withdrawal_recipient_iban_input_label')}
              </label>
            </div>
          )}
          {requiredFields.includes('recipientBic') && (
            <div className="form-group mt-5 mb-0">
              <input
                className="form-control-input"
                placeholder={translate('fiat_withdrawal_recipient_bic_input_label')}
                value={form.recipientBic}
                onChange={handleChange}
                name="recipientBic"
                onBlur={handleBlur}
                required
              />
              <label className="floating-label">
                {translate('fiat_withdrawal_recipient_bic_input_label')}
              </label>
            </div>
          )}
          {requiredFields.includes('recipientAccountNumber') && (
            <div className="form-group mt-5 mb-0">
              <input
                className="form-control-input"
                placeholder={translate('fiat_withdrawal_recipient_account_input_label')}
                value={form.recipientAccountNumber}
                onChange={handleChange}
                name="recipientAccountNumber"
                onBlur={handleBlur}
                required
              />
              <label className="floating-label">
                {translate('fiat_withdrawal_recipient_account_input_label')}
              </label>
            </div>
          )}
          {requiredFields.includes('recipientSortCode') && (
            <div className="form-group mt-5 mb-0">
              <input
                className="form-control-input"
                placeholder={translate('fiat_withdrawal_recipient_sort_code_label')}
                value={form.recipientSortCode}
                onChange={handleChange}
                name="recipientSortCode"
                onBlur={handleBlur}
                required
              />
              <label className="floating-label">
                {translate('fiat_withdrawal_recipient_sort_code_label')}
              </label>
            </div>
          )}
          {requiredFields.includes('recipientBank') && (
            <div className="form-group mt-5 mb-0">
              <input
                className="form-control-input"
                placeholder={translate('fiat_withdrawal_recipient_bank_input_label')}
                value={form.recipientBank}
                onChange={handleChange}
                name="recipientBank"
                onBlur={handleBlur}
                required
              />
              <label className="floating-label">
                {translate('fiat_withdrawal_recipient_bank_input_label')}
              </label>
            </div>
          )}
          {requiredFields.includes('recipientBankCountry') && (
            <div className="select-group form-group my-0 mt-5 form_group-container">
              <SelectCountryDropdown
                name="recipientBankCountry"
                placeholder={translate('fiat_withdrawal_recipient_bank_country_input_label')}
                getOptionLabel={(option) => option.displayName}
                isOptionSelected={(option) => option.id === form.recipientBankCountry}
                value={form.recipientBankCountry}
                onChange={(data, name) => handleChange(data.id, name)}
                handleBlur={handleBlur}
                clearError={clearError}
                optionName="displayName"
                prefix=""
                containerClass="w-100 position-relative bg-lighter p-3 mt-2 rounded shadow-lg"
                fieldsInfo={selectDropDownFields}
                dropDownParentClass=""
              />
              <label className="floating-label">
                {translate('fiat_withdrawal_recipient_bank_country_input_label')}
              </label>
            </div>
          )}
        </form>
      </div>
    </ModalBodyLayout>
  );
};

export default FiatWalletWithdrawalDetails;
FiatWalletWithdrawalDetails.propTypes = {
  options: PropTypes.object,
  openScreen: PropTypes.func.isRequired,
  onCloseModal: PropTypes.func.isRequired,
};

FiatWalletWithdrawalDetails.defaultProps = {
  options: null,
};
